import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { apiFetch } from '../../config/api'
import { fetchImportSourcePdfUrl, triggerBrowserDownload } from '../../utils/importReportPdf'
import { getReportDownloadUrl } from '../../utils/reportsClient'

export interface AskEvansReportDownload {
  projectId: string
  pipeSetId: string
  reportId: string
  fileName: string
  kind: 'pqr' | 'wps'
  pqrNumber?: string
  importDraft?: boolean
  extractionId?: string
}

export interface AskEvansCitation {
  id: string
  kind?: string
  label?: string
  href?: string
  download?: AskEvansReportDownload
}

function isProject(citation: AskEvansCitation) {
  return citation.kind === 'project' || citation.kind === 'search_projects' || citation.kind === 'get_project'
}

function fallback(citation: AskEvansCitation, projectName?: string | null) {
  if (citation.label) return citation.label
  if (isProject(citation)) {
    return projectName ? `Open project ${projectName}.` : 'Open this project.'
  }
  if (citation.kind === 'pipe_set' || citation.kind === 'list_pipe_sets' || citation.kind === 'get_pipe_set') {
    return 'This pipe set was used in the answer.'
  }
  return 'This record was used in the answer.'
}

function hrefFor(citation: AskEvansCitation) {
  if (citation.href) return citation.href
  if (isProject(citation) && citation.id) return `/projects/${encodeURIComponent(citation.id)}/next`
  return undefined
}

function DownloadButton({ download, label }: { download: AskEvansReportDownload; label: string }) {
  const [busy, setBusy] = useState(false)

  async function onDownload() {
    if (busy) return
    setBusy(true)
    try {
      if (download.importDraft) {
        if (!download.extractionId) {
          throw new Error('This report has no PDF file yet')
        }
        const file = await fetchImportSourcePdfUrl({
          projectId: download.projectId,
          pipeSetId: download.pipeSetId,
          extractionId: download.extractionId,
          kind: download.kind,
          pqrNumber: download.pqrNumber,
          fileName: download.fileName
        })
        try {
          await triggerBrowserDownload(file.downloadUrl, file.fileName)
        } finally {
          URL.revokeObjectURL(file.downloadUrl)
        }
      } else {
        const { downloadUrl } = await getReportDownloadUrl({
          projectId: download.projectId,
          pipeSetId: download.pipeSetId,
          reportId: download.reportId
        })
        await triggerBrowserDownload(downloadUrl, download.fileName)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not download report')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center justify-between gap-3">
      <p className="text-[12px] leading-relaxed text-text-grey">{label}</p>
      <button
        type="button"
        disabled={busy}
        onClick={() => void onDownload()}
        className="shrink-0 rounded-md border border-blueprint-blue px-2.5 py-1 text-[11px] font-semibold text-blueprint-blue hover:bg-blueprint-blue hover:text-white disabled:opacity-60"
      >
        {busy ? 'Preparing…' : 'Download'}
      </button>
    </div>
  )
}

function CitationRow({ citation }: { citation: AskEvansCitation }) {
  const [projectName, setProjectName] = useState<string | null>(null)

  useEffect(() => {
    if (citation.label || !isProject(citation) || !citation.id) return
    let cancelled = false
    apiFetch<{ name?: string; number?: string }>(`/api/projects/${encodeURIComponent(citation.id)}`)
      .then((project) => {
        if (cancelled || !project?.name) return
        setProjectName(project.number ? `${project.name} (${project.number})` : project.name)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [citation])

  const text = fallback(citation, projectName)
  if (citation.download) {
    return <DownloadButton download={citation.download} label={text} />
  }
  const href = hrefFor(citation)
  if (!href) {
    return <p className="text-[12px] leading-relaxed text-text-grey">{text}</p>
  }
  return (
    <a href={href} className="block text-[12px] font-medium leading-relaxed text-blueprint-blue underline hover:text-hero-red">
      {text}
    </a>
  )
}

export function Citations({ citations }: { citations?: AskEvansCitation[] | null }) {
  if (!citations?.length) return null
  const seen = new Set<string>()
  const rows = citations.filter((citation) => {
    if (!citation.label && !citation.href && !citation.download && !isProject(citation)) return false
    const key = `${citation.kind || ''}:${citation.id}:${citation.label || ''}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  return (
    <div className="mt-2 space-y-1">
      {rows.map((citation) => (
        <CitationRow key={`${citation.kind}-${citation.id}-${citation.label || ''}`} citation={citation} />
      ))}
    </div>
  )
}
